import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

const CATS = ["general", "sleep", "exercise", "productivity", "nutrition", "mindfulness", "social", "learning", "other"];
const TAGS = ["basics", "forgot", "no_time", "physically_sick", "emergency", "mental_emotional", "stop", "rewards", "relapse"];
const inputCls = "w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none";

export function ArticleEditor({ initial, onSave, onCancel }) {
  const [f, setF] = useState(
    initial
      ? { ...initial, bodyText: initial.body.join("\n\n") }
      : { title: "", summary: "", bodyText: "", category: "general", tags: [], read_minutes: 3, sources: [], visibility: "my_clients" }
  );
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleTag = (t) => set("tags", f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t]);

  const submit = (e) => {
    e.preventDefault();
    const body = f.bodyText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    if (!f.title.trim() || !f.summary.trim() || body.length === 0) return;
    onSave({
      title: f.title, summary: f.summary, body, category: f.category, tags: f.tags,
      read_minutes: Number(f.read_minutes) || 3, visibility: f.visibility,
      sources: f.sources.filter((s) => s.title.trim() && s.url.trim()),
    });
  };

  return (
    <form onSubmit={submit} className="warm-card p-6 space-y-3" data-testid="article-editor">
      <h3 className="font-display font-bold text-stone-900">{initial ? "Edit article" : "New article for the people you support"}</h3>
      <input data-testid="article-title" className={inputCls} placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} />
      <input data-testid="article-summary" className={inputCls} placeholder="One-line summary" value={f.summary} onChange={(e) => set("summary", e.target.value)} />
      <textarea data-testid="article-body" className={inputCls} rows={6} placeholder="Body — separate paragraphs with a blank line" value={f.bodyText} onChange={(e) => set("bodyText", e.target.value)} />
      <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="article-category" className={inputCls + " w-auto"} value={f.category} onChange={(e) => set("category", e.target.value)}>
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input data-testid="article-minutes" type="number" min={1} max={60} className={inputCls + " w-24"} value={f.read_minutes} onChange={(e) => set("read_minutes", e.target.value)} />
        <span className="text-xs text-stone-500">min read</span>
        <select data-testid="article-visibility" className={inputCls + " w-auto"} value={f.visibility} onChange={(e) => set("visibility", e.target.value)}>
          <option value="my_clients">Only my people you support</option>
          <option value="all">Everyone</option>
        </select>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-1.5">Tags (drive smart suggestions)</div>
        <div className="flex flex-wrap gap-1.5">
          {TAGS.map((t) => (
            <button key={t} type="button" data-testid={`article-tag-${t}`} onClick={() => toggleTag(t)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold ${f.tags.includes(t) ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}>
              {t.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">External sources</div>
        {/* index key is intentional: sources are positional, editable rows without ids and are never reordered */}
        {f.sources.map((s, i) => (
          <div key={`source-${i}`} className="flex gap-2">
            <input className={inputCls} placeholder="Source title" value={s.title} onChange={(e) => set("sources", f.sources.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
            <input className={inputCls} placeholder="https://…" value={s.url} onChange={(e) => set("sources", f.sources.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} />
            <button type="button" onClick={() => set("sources", f.sources.filter((_, j) => j !== i))} className="text-stone-400 hover:text-stone-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" data-testid="article-add-source" onClick={() => set("sources", [...f.sources, { title: "", url: "" }])} className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add source link
        </button>
      </div>
      <div className="flex gap-2">
        <button data-testid="article-save" type="submit" className="amber-btn text-sm">Save article</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-semibold bg-stone-100 text-stone-700">Cancel</button>
      </div>
    </form>
  );
}

export function ResourceEditor({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { title: "", url: "", description: "", category: "general", visibility: "my_clients" });
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim() || !/^https?:\/\//.test(f.url)) return;
    onSave({ title: f.title, url: f.url, description: f.description, category: f.category, visibility: f.visibility });
  };
  return (
    <form onSubmit={submit} className="warm-card p-6 space-y-3" data-testid="resource-editor">
      <h3 className="font-display font-bold text-stone-900">{initial ? "Edit link" : "Add an external resource"}</h3>
      <input data-testid="resource-title" className={inputCls} placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} />
      <input data-testid="resource-url" className={inputCls} placeholder="https://…" value={f.url} onChange={(e) => set("url", e.target.value)} />
      <input data-testid="resource-description" className={inputCls} placeholder="Why is this worth reading?" value={f.description} onChange={(e) => set("description", e.target.value)} />
      <div className="flex gap-2">
        <select data-testid="resource-category" className={inputCls + " w-auto"} value={f.category} onChange={(e) => set("category", e.target.value)}>
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="resource-visibility" className={inputCls + " w-auto"} value={f.visibility} onChange={(e) => set("visibility", e.target.value)}>
          <option value="my_clients">Only my people you support</option>
          <option value="all">Everyone</option>
        </select>
      </div>
      <div className="flex gap-2">
        <button data-testid="resource-save" type="submit" className="amber-btn text-sm">Save link</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-semibold bg-stone-100 text-stone-700">Cancel</button>
      </div>
    </form>
  );
}
